import { PaddedContainer } from '@/atoms/PaddedContainer'
import { paths } from '@/constants/paths'
import { LegalLayout } from '@/layouts/LegalLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { getGenericStaticPaths } from '@/utils/nextUtils/staticPages'
import { loadTranslations } from '@/utils/translate/translate-server'

const orderedListAlpha = 'list-[upper-roman] ml-4'
const headerClasses = 'inline font-bold'

const TermsOfUsePage: NextPageWithLayout = () => {
  return (
    <div className="bg-white py-20 text-justify text-black">
      <PaddedContainer>
        <div className="flex flex-col items-center gap-8 p-10">
          <header className="text-center text-[1.3rem] font-bold md:text-[2rem]">
            <p data-testid="company">Angel Studios</p>
            <h1 data-testid="terms-of-use-header">ANGEL GUILD MEMBERSHIP TERMS AND CONDITIONS</h1>
          </header>
          <p>Effective Date: October 11, 2023</p>
          <p>
            These Terms and Conditions (these “Terms”) apply to purchases made by you in connection with your Angel
            Guild Membership (“Membership”) and associated Perks Program (the “Program”) of Angel Studios, Inc. (“Angel
            Studios”). By participating in the Program, you agree to be bound by these Terms.
          </p>
          <ol className={orderedListAlpha}>
            <li className="mb-4">
              <h2 className={headerClasses}>Membership Benefits.</h2>
              <ol type="a" className="ml-4 list-[lower-alpha]">
                <li>
                  <span className="underline">Voting on Shows</span>. Your Membership allows you to screen proof of
                  concept films (called “Torches”) an finished films/episodes to help ensure audience demand and mission
                  alignment (i.e., “amplifying light”) for all Angel Studios projects. Angel maintains the right to
                  modify the voting process, use the data from the voting, and adjust the minimum thresholds required to
                  pass the Guild in Angel’s sole and absolute discretion.
                </li>
                <li>
                  <span className="underline">Early Access inside the Angel App</span>. Once a film or episode of a
                  series is available for viewing inside the Angel app, Members will be permitted limited early access
                  to the film or series prior to the general public being given access inside the Angel App. Early
                  access could be anywhere from 24 hours to several weeks before the general Angel app access depending
                  on how windows with third party platforms are negotiated and agreed.
                </li>
                <li>
                  <span className="underline">Free Movie Tickets (on opening weekend)</span>. Members will receive
                  coupon codes to redeem two (2) free tickets ($30 value) to opening weekend of each theatrical release
                  by Angel Studios during the time of their Membership. Tickets redeemed or claimed are valid at
                  participating theaters using Atom ticketing. Coupons after opening weekend will be void and the value
                  will not roll over into the next subsequent theatrical release.
                </li>
                <li>
                  <span className="underline">Discount on Merchandise</span>. Members will receive a discount on select
                  Angel merchandise inside the Angel online store. Angel reserves the right to determine which items of
                  merchandise are available for the discount and the timing and duration and value of such discounts in
                  its sole discretion. All discounts are subject to change at any time in Angel’s sole discretion.
                </li>
                <li>
                  <span className="underline">Other Offers</span>. Angel may partner with other companies to expand or
                  increase its perks for Members and will determine the duration, value and method of redemption for
                  such perks (if any).
                </li>
              </ol>
            </li>
            <li className="mb-4">
              <h2 className={headerClasses}>Conditions for Membership in the Guild; Additional Terms.</h2> To become an
              Angel Guild member, you must create an Angel Studios account at{' '}
              <a className="text-blue-500 underline" href="https://www.angel.com/guild">
                www.angel.com/guild
              </a>
              . You agree and acknowledge that you have complied with and will continue to comply with the Angel Studios{' '}
              <a className="text-blue-500 underline" href={paths.legal.termsOfUse}>
                Terms of Use
              </a>{' '}
              and{' '}
              <a className="text-blue-500 underline" href={paths.legal.guildTerms}>
                Terms of Service
              </a>
              , and any other terms relating to the creation of an Angel Studios account.
            </li>
            <li className="mb-4">
              <h2 className={headerClasses}>Indemnification.</h2> You agree to indemnify Angel Studios for any costs,
              damages and expenses (including reasonable attorneys’ fees) arising from or relating to: (a) any claim or
              cause of action brought by a third party against Angel Studios as a result of your Membership, your
              redemption of any Perk, (b) your breach of these Terms, (c) the Angel Studios{' '}
              <a className="text-blue-500 underline" href={paths.legal.termsOfUse}>
                Terms of Use
              </a>
              , or (d) the Angel Studios{' '}
              <a className="text-blue-500 underline" href={paths.legal.guildTerms}>
                Terms of Service
              </a>
              .
            </li>
            <li className="mb-4">
              <h2 className={headerClasses}>LIMITATION OF LIABILITY AND RELEASE.</h2> By participating in the Angel
              Guild Membership Program, you agree to the full extent permitted by applicable law to release and hold
              harmless Angel Studios and its affiliates and its and their officers, directors, employees, agents and
              representatives (the “Released Parties”) from and against any claim or cause of action of any kind,
              whether known or unknown, of any kind, arising from or relating to your participation in the Program,
              including without limitation your creation of an account with Angel Studios, your provision of personal
              information to Angel Studios, and your receipt and redemption of a Perk. You further agree that in any
              such claim or cause of action, the liability of the Released Parties will be limited to your out-of-pocket
              costs of your Membership. In no event will the Released Parties be liable for attorneys’ fees or punitive,
              consequential, indirect or director damages whatsoever.
            </li>
            <li className="mb-4">
              <h2 className={headerClasses}>Choice of Law; Venue.</h2> These Terms and the Program shall be governed by
              and construed according to the laws of the State of Utah, without regard to any conflict of laws rules
              that would cause the application of the laws of another jurisdiction. All claims, actions and disputes
              arising out of or relating to these Terms and the Program will be resolved exclusively by the courts
              located in Salt Lake County, Utah.
            </li>
            <li>
              <h2 className={headerClasses}>WAIVER OF CLASS ACTION.</h2>{' '}
              <b>
                All claims, actions and disputes shall be brought individually and you hereby waive, to the maximum
                extent permissible under applicable law, any rights to bring or participate in a class action.
              </b>
            </li>
            <li>
              <h2 className={headerClasses}>Survival.</h2> If, for any reason, any provision of these Terms is deemed
              invalid or unenforceable, such provisions shall be enforced to the maximum extent permitted by applicable
              law and the remaining provisions shall remain in full force and effect.
            </li>
            <li>
              <h2 className={headerClasses}>No Waiver by Angel Studios.</h2> The failure by Angel Studios to enforce any
              provision or any rights provided under these Terms shall not constitute a waiver of such provision or
              rights. Any waiver is effective only if made in writing and signed by Angel Studios.
            </li>
            <li>
              <h2 className={headerClasses}>Amendment.</h2> Angel Studios may amend these Terms at any time and for any
              reason without your approval. Your continued participation in the Program will constitute your acceptance
              of the amended Terms.
            </li>
            <li>
              <h2 className={headerClasses}>Headings.</h2> Headings used in these Terms shall not be used to interpret
              the contents herein.
            </li>
          </ol>
        </div>
      </PaddedContainer>
    </div>
  )
}

TermsOfUsePage.getLayout = (page) => {
  return <LegalLayout>{page}</LegalLayout>
}

export const getStaticProps = getStaticPropsErrorHandler(
  { filePath: 'pages/[locale]/legal/guild/membership/index.tsx' },
  async ({ locale }) => {
    return {
      props: {
        ...(await loadTranslations(locale, ['common', 'account'])),
      },
      revalidate: false,
    }
  },
)

export const getStaticPaths = getGenericStaticPaths

export default TermsOfUsePage
